<template>
  <gjw-dialog
    class="progress-dialog"
    :show-dialog="dialogVisible"
    :config="{ showFooter: false, width: '300px' }">
    <div class="progress-container">
      <span>{{ label }}</span>
      <el-progress :percentage="percentage" :stroke-width="10" :show-text="false"></el-progress>
    </div>
  </gjw-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { localCachePath, checkExistFile } from '@/core/desktopCache'
import { getResourceType } from '@/assets/js/resourceMap'

export default {
  name: 'LoadProgressBarDialog',
  props: {
    resourceType: {
      type: String,
      default: 'COMPONENT'
    },
    label: {
      type: String,
      default: '正在载入'
    },
    increment: {
      type: Number,
      default: 10
    },
    selectedList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      evtNameMap: {
        COMPONENT: 'Component',
        MODULE: 'Module',
        SCHEME: 'Scheme',
      },
      dialogVisible: false,
      saveAsPath: '',
      percentage: 0,
      spaceId: null,
      resourceInfo: null,
      progressStatus: null,
      successNumber: 0,
      errorNumber: 0,
      subdivideType: '',
      familyFoundStrategy: '',
      batchDownloadStatus: false,
      batchSuccess: false,
      batchData: [],
      // 桥梁/道路软件-批量载入的构件数据
      capitalBatchLoadComponents: [],
    }
  },
  computed: {
    ...mapGetters({
      parameterTypeOptions: 'resourceDetail/parameterTypeOptions',
      versionResourceInfo: 'resourceDetail/versionResourceInfo',
      user: 'user/user',
      isCapitalSoftware: 'isCapitalSoftware',
      softwareCode: 'softwareCode'
    }),
    resourceCn() {
      return getResourceType(this.resourceType)
    },
    isAppGbr() {
      return this.softwareCode === 'AppGbr'
    },
    evtName() {
      return this.evtNameMap[this.resourceType]
    },
    withoutUI() {
      return this.isCapitalSoftware && this.resourceType === 'COMPONENT'
    },
  },
  methods: {
    ...mapActions({
      loadVersionResourceInfo: 'resourceDetail/loadVersionResourceInfo'
    }),
    mergeArraysById(array1, array2) {
      const mergedMap = new Map()
      array1.forEach((item) => {
        mergedMap.set(item.id, { ...item })
      })
      array2.forEach((item) => {
        if (mergedMap.has(item.id)) {
          mergedMap.set(item.id, { ...item, ...mergedMap.get(item.id) })
        }
      })
      return Array.from(mergedMap.values())
    },
    init(spaceId, subdivideType) {
      this.dialogVisible = true
      this.spaceId = spaceId
      this.successNumber = 0
      this.errorNumber = 0
      this.percentage = 0
      this.subdivideType = subdivideType
      this.capitalBatchLoadComponents = []
    },
    async openDialog(spaceId, row, subdivideType, familyFoundStrategy) {
      this.init(spaceId, subdivideType)
      this.resourceInfo = row
      this.familyFoundStrategy = familyFoundStrategy
      await this.loadVersionResourceInfo({
        space_id: spaceId,
        space_resource_id: row.spaceResourceId,
        resource_software_version_id: row.id,
      })
      await this.download()
    },
    async batchOpenDialog(spaceId, subdivideType, data, batchSelectData) {
      this.init(spaceId, subdivideType)
      this.batchData = this.mergeArraysById(data, batchSelectData)
      // 批量载入埋点
      this.$tracks({
        event: `loadSpace${this.evtName}`,
        params: {
          g_c_space_id: this.spaceId,
          g_c_file_id: this.batchData.map((f) => f.id),
          g_c_batch: true,
          g_c_count: this.batchData.length,
        }
      })
      if (this.withoutUI || !this.batchSuccess) {
        for (let i = 0; i < this.batchData.length; i++) {
          const item = this.batchData[i]
          await this.download(item.downloadUrl, item)
        }
        console.log('END')
        if (!this.withoutUI) {
          this.batchSuccess = true
        }
      }
    },
    async beforeDownload(row) {
      const catchPath = localCachePath()
      const type = this.resourceType === 'COMPONENT' ? '' : `${this.resourceType}_`
      this.saveAsPath = `${catchPath}\\${type}${this.versionResourceInfo.dataVersionId || row.dataVersionId}`
      // console.log('下载资源前获取插件下载地址, 保存路径：', this.saveAsPath)
      return await checkExistFile(this.saveAsPath)
    },
    // 获取批量操作的operationKey
    getOperationKey(selectKey = 'id') {
      return new Promise((resolve, reject) => {
        const contentIds = this.selectedList.map((item) => { return item[selectKey] })
        this.$api.resource.getBatchOperationKey(this.spaceId, contentIds).then((data) => {
          return resolve(data)
        }).catch((err) => {
          reject(err)
        })
      })
    },
    async download(url, row) {
      try {
        if (this.subdivideType === 'single' && !this.withoutUI && await this.beforeDownload(row)) {
          console.log('本地有缓存，跳过下载，精度条100')
          this.percentage = 100
          this.progressStatus = 'success'
          if (this.resourceType === 'COMPONENT') {
            this.onComponentLoad()
          } else if (this.resourceType === 'MODULE') {
            this.onModuleLoad()
          } else if (this.resourceType === 'SCHEME') {
            this.onSchemeLoad()
          }
          return
        }
        if (this.subdivideType === 'single') {
          const catchPath = localCachePath()
          const type = this.resourceType === 'COMPONENT' ? '' : `${this.resourceType}_`
          this.saveAsPath = `${catchPath}\\${type}${this.versionResourceInfo.dataVersionId || row.dataVersionId}`
          const downloadUrl = await this.$api.resource.getDownloadUrl(this.spaceId, this.resourceInfo.spaceResourceId, this.versionResourceInfo.id, this.versionResourceInfo.dataVersionId)
          await this.downloadFile(downloadUrl)
        } else if (this.subdivideType === 'batch') {
          await this.downloadFile(url, row)
        }
      } catch (err) {
        this.errorNumber += 1
        this.progressStatus = 'exception'
        !this.withoutUI && this.taskComplete()
      }
    },
    async downloadFile(downloadUrl, row) {
      if (downloadUrl) {
        try {
          const catchPath = localCachePath()
          const type = this.resourceType === 'COMPONENT' ? '' : `${this.resourceType}_`
          const saveAsPath = row ? `${catchPath}\\${type}${row.dataVersionId}` : this.saveAsPath
          await this.$desktopClient.DownloadFile(downloadUrl, saveAsPath, true)
          if (this.subdivideType === 'single') {
            this.percentage = 100
          } else if (this.withoutUI) {
            // 基建软件下载完就进度条更新
            this.percentage < 95 && (this.percentage += 5)
          }
          this.progressStatus = 'success'
          if (this.resourceType === 'COMPONENT') {
            await this.onComponentLoad(row)
          } else if (this.resourceType === 'MODULE') {
            this.onModuleLoad()
          } else if (this.resourceType === 'SCHEME') {
            this.onSchemeLoad()
          }
        } catch (err) {
          console.error(err)
          this.errorNumber += 1
          this.progressStatus = 'exception'
          !this.isCapitalSoftware && this.taskComplete()
        }
      } else {
        this.errorNumber += 1
        this.progressStatus = 'exception'
        !this.isCapitalSoftware && this.taskComplete()
      }
    },
    tooltipMessageBox() {
      if (this.isCapitalSoftware) {
        return
      }
      if (this.subdivideType === 'single') {
        if (this.successNumber > 0) {
          this.$message.success(`${this.resourceCn}载入成功`)
        } else {
          this.$message.error(`${this.resourceCn}载入失败`)
        }
        return
      }
      let message = ''
      let type
      if (this.successNumber > 0) {
        message += `<div>${this.successNumber}个${this.resourceCn}载入成功。</div>`
      }
      if (this.errorNumber > 0) {
        message += `<div>${this.errorNumber}个${this.resourceCn}载入失败，请稍后重试。</div>`
      }

      if (this.successNumber > 0 && this.errorNumber === 0) {
        type = 'success'
      } else if (this.successNumber === 0 && this.errorNumber > 0) {
        type = 'error'
      } else {
        type = 'info'
      }

      this.$confirm(
        `<div>${message}</div>`,
        {
          confirmButtonText: '确定',
          showClose: false,
          showCancelButton: false,
          type,
          dangerouslyUseHTMLString: true
        }
      )
        .then(() => {
          this.batchDownloadStatus = false
          this.batchSuccess = false
          this.$emit('clearSelection')
        })
        .catch(() => {
          this.dialogVisible = false
          this.batchDownloadStatus = false
          this.batchSuccess = false
          this.$emit('clearSelection')
        })
    },
    taskComplete() {
      if (this.subdivideType === 'single' || this.successNumber + this.errorNumber === this.batchData.length) {
        if (this.successNumber > 0 || this.errorNumber > 0) {
          this.tooltipMessageBox()
        }
      }
    },
    async loadComponent(params) {
      try {
        const res = await this.$desktopClient.LoadComponent(params)
        if (res.code === 'success' && res.data) {
          this.successNumber += 1
          this.percentage < 95 && (this.percentage += 5)
        } else {
          this.errorNumber += 1
          this.progressStatus = 'exception'
        }
        this.taskComplete()
        if (this.successNumber + this.errorNumber === this.batchData.length) {
          this.percentage = 100
          this.dialogVisible = false
        }
      } finally {
        this.subdivideType === 'single' && (this.dialogVisible = false)
      }
    },
    // 桥梁构件单个/批量载入；道路批量载入
    async loadGbrComponent() {
      // 判断已勾选的数量是否全部都已下载完成
      const batchDownOk = this.capitalBatchLoadComponents.length + this.errorNumber === this.batchData.length
      const canLoad = this.subdivideType === 'single' || batchDownOk
      if (canLoad) {
        await this.$desktopClient.LoadComponents(this.capitalBatchLoadComponents)
        this.dialogVisible = false
        this.$emit('clearSelection')
      }
    },
    async onComponentLoad(row) {
      if (this.subdivideType === 'single') { // 单个载入
        this.$tracks({
          event: `loadSpace${this.evtName}`,
          params: {
            g_c_space_id: this.spaceId,
            g_c_file_id: this.resourceInfo.spaceResourceId,
            g_c_batch: false,
            g_c_fimaliy_type: this.radio
          }
        })
        if (this.isAppGbr) {
          // 桥梁软件单个载入
          const params = {
            userId: this.user.strId,
            softwareVersionId: this.resourceInfo.softwareVersionId,
            dataVersionId: this.resourceInfo.dataVersionId,
            name: this.resourceInfo.name,
            suffix: this.resourceInfo.suffix,
            filePath: this.saveAsPath,
          }
          this.capitalBatchLoadComponents.push(params)
          await this.loadGbrComponent()
        } else {
          const params = {
            userId: this.user.strId,
            softwareVersionId: this.resourceInfo.softwareVersionId,
            dataVersionId: this.resourceInfo.dataVersionId,
            name: this.resourceInfo.name,
            suffix: this.resourceInfo.suffix,
            filePath: this.saveAsPath,
            familyId: this.resourceInfo.familyId,
            familyFoundStrategy: this.familyFoundStrategy || 'FFS_OVERWRITE'
          }
          await this.loadComponent(params)
        }
      } else if (this.subdivideType === 'batch') {
        const catchPath = localCachePath() // 批量载入
        if (this.isCapitalSoftware) {
          const params = {
            userId: this.user.strId,
            softwareVersionId: row.softwareVersionId,
            dataVersionId: row.dataVersionId,
            name: row.name,
            suffix: row.suffix,
            filePath: `${catchPath}\\${row.dataVersionId}`,
          }
          this.capitalBatchLoadComponents.push(params)
          await this.loadGbrComponent()
        } else {
          const params = {
            userId: this.user.strId,
            softwareVersionId: row.softwareVersionId,
            dataVersionId: row.dataVersionId,
            name: row.name,
            suffix: row.suffix,
            filePath: `${catchPath}\\${row.dataVersionId}`,
            familyId: row.familyId,
            familyFoundStrategy: row.reName === true ? 'FFS_CREATE' : 'FFS_OVERWRITE' || 'FFS_OVERWRITE'
          }
          await this.loadComponent(params)
        }
      }
    },
    onModuleLoad() {
      try {
        this.$tracks({
          event: `loadSpace${this.evtName}`,
          params: {
            g_c_space_id: this.spaceId,
            g_c_file_id: this.resourceInfo.spaceResourceId,
          }
        })

        this.$desktopClient.LoadModule({
          userId: this.user.strId,
          softwareVersionId: this.resourceInfo.softwareVersionId,
          dataVersionId: this.resourceInfo.dataVersionId,
          name: this.resourceInfo.name,
          suffix: this.resourceInfo.suffix,
          filePath: this.saveAsPath,
          // guid: String, 主模块GUID
          // type: String, 模块类型
          // functionType: String, 功能类型
          // specialtyType: String, 专业类型
          // moduleName: String, 模块名
          // moduleFileName: String, 模块文件名
          // metaJson: String, 模块meta.json内容
        }).then((res) => {
          if (res.code === 'success') {
            this.$message.success('模块载入成功')
          } else {
            this.$message.error('模块载入失败')
          }
        }).finally(() => {
          this.dialogVisible = false
        })
      } catch (err) {
        // 载入失败
        console.error(err)
      }
    },
    onSchemeLoad() {
      try {
        this.$tracks({
          event: `loadSpace${this.evtName}`,
          params: {
            g_c_space_id: this.spaceId,
            g_c_file_id: this.resourceInfo.spaceResourceId,
          }
        })
        this.$desktopClient.LoadTemplate({
          filePath: this.saveAsPath,
        }).then((res) => {
          if (res.code === 'success') {
            this.$message.success(`模板载入成功`)
          } else {
            this.$message.error(`模板载入失败`)
          }
        }).finally(() => {
          this.dialogVisible = false
        })
      } catch (err) {
        // 载入失败
        console.error(err)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.progress-dialog {
  .progress-container {
    display: flex;
    align-items: baseline;
    span {
      padding-right: 10px;
    }
    .el-progress {
      flex: 1
    }
  }
  /deep/ .el-dialog__header {
    display: none;
  }
}
</style>
